// src/js/core/wordNormalizer.js

/**
 * When users click words in dialogues to save them,
 * this file ensures words like "cat" and "cat!"
 * are recognized as the same word in their saved words list.
 */

// Punctuation marks that should be removed as they're not part of words
const PUNCTUATION_TO_REMOVE = [
  // Basic sentence punctuation
  ".",
  ",",
  "!",
  "?",
  ";",
  ":",

  // Quotes (Western)
  '"',
  "\u201C", // Opening double quote (“)
  "\u201D", // Closing double quote (”)
  "\u2018", // Opening single quote (‘)
  "\u2019", // Closing single quote (’)

  // Brackets and parentheses
  "(",
  ")",
  "[",
  "]",
  "{",
  "}",

  // Other marks
  "...",
  "…", // Ellipsis (U+2026)
  "/",
  "\\",

  // Spanish/Portuguese opening punctuation
  "¡", // (U+00A1)
  "¿", // (U+00BF)

  // French / German / Russian quotes etc.
  "«", // Guillemet Left (U+00AB)
  "»", // Guillemet Right (U+00BB)
  "„", // Double Low-9 Quote (U+201E) - German, Russian etc.

  // Chinese/Japanese punctuation
  "。", // Ideographic Full Stop (U+3002)
  "，", // Fullwidth Comma (U+FF0C)
  "、", // Ideographic Comma (U+3001)
  "！", // Fullwidth Exclamation Mark (U+FF01)
  "？", // Fullwidth Question Mark (U+FF1F)
  "：", // Fullwidth Colon (U+FF1A) - Chinese
  "；", // Fullwidth Semicolon (U+FF1B) - Chinese
  "「", // Left Corner Bracket (U+300C)
  "」", // Right Corner Bracket (U+300D)
  "『", // Left White Corner Bracket (U+300E)
  "』", // Right White Corner Bracket (U+300F)
  "（", // Fullwidth Left Parenthesis (U+FF08)
  "）", // Fullwidth Right Parenthesis (U+FF09)
  "《", // Left Double Angle Bracket (U+300A)
  "》", // Right Double Angle Bracket (U+300B)
  "【", // Left Black Lenticular Bracket (U+3010) - Chinese, Japanese
  "】", // Right Black Lenticular Bracket (U+3011) - Chinese, Japanese
  "・", // Katakana Middle Dot (U+30FB)

  // Arabic punctuation
  "،", // Arabic comma (U+060C)
  "؛", // Arabic semicolon (U+061B)
  "؟", // Arabic question mark (U+061F)
];

/**
 * Normalizes a word by removing surrounding punctuation while preserving
 * word-essential characters like apostrophes in contractions and hyphens
 * in compound words.
 *
 * @param {string} word - The word to normalize
 * @param {string} language - The language of the text (for language-specific handling)
 * @returns {string} - The normalized word
 */
export function normalizeWord(word, language = "english") {
  // Convert to lowercase
  let normalized = word.toLowerCase();

  // Remove surrounding punctuation
  for (const punct of PUNCTUATION_TO_REMOVE) {
    normalized = normalized.split(punct).join("");
  }

  // Trim whitespace
  normalized = normalized.trim();

  // Language-specific handling
  if (language === "arabic") {
    // Remove Arabic diacritical marks (تشكيل) as they indicate pronunciation
    // but aren't part of the base word
    normalized = normalized.replace(/[\u064B-\u065F\u0670]/g, "");
  }

  return normalized;
}

/**
 * Checks if a string contains only punctuation marks
 * @param {string} word - The string to check
 * @returns {boolean} - True if the string consists only of punctuation
 */
export function isOnlyPunctuation(word) {
  const punctRegex = new RegExp(
    `^[${PUNCTUATION_TO_REMOVE.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}\\s]*$`
  );
  return punctRegex.test(word);
}

/**
 * Splits text into words while handling language-specific cases
 * @param {string} text - The text to split
 * @param {string} language - The language of the text
 * @returns {string[]} - Array of words
 */
export function splitIntoWords(text, language = "english") {
  // For all languages, split on whitespace
  return text.split(/\s+/).filter((word) => word.length > 0);
}
